import { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { Plus, Pencil, Trash2, Users, Image as ImageIcon } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const DEPT_OPTIONS = ['상품개발본부', '소재개발본부', '장비개발본부', '경영지원', '기타'];
const TEAM_OPTIONS = { '상품개발본부': ['신바나팀', '머천다이즈팀'] };
const RANK_OPTIONS = ['대표이사', '대표', '본부장', '팀장', '과장', '차장', '대리', '주임', '사원'];

function LoginBgUpload() {
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setPreview(`/uploads/settings/login-bg.jpg?t=${Date.now()}`);
    img.src = `/uploads/settings/login-bg.jpg?t=${Date.now()}`;
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setStatus('업로드 중...');
    const fd = new FormData();
    fd.append('image', file);
    try {
      await api.post('/settings/login-bg', fd);
      setStatus('저장됨 ✓');
    } catch { setStatus('실패'); }
  };

  const remove = async () => {
    await api.delete('/settings/login-bg');
    setPreview(null);
    setStatus('삭제됨');
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
      <p className="text-gray-400 text-sm font-semibold mb-3">로그인 배경 이미지</p>
      <div
        onClick={() => fileInputRef.current?.click()}
        className="relative border border-dashed border-gray-700 hover:border-gray-500 rounded-lg overflow-hidden cursor-pointer h-32 flex items-center justify-center transition-colors"
      >
        {preview ? (
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-600">
            <ImageIcon size={24} />
            <span className="text-xs">클릭하여 이미지 선택</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="text-white text-xs">변경</span>
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={upload} />
      <div className="flex items-center justify-between mt-2">
        {status && <span className="text-gray-500 text-xs">{status}</span>}
        <button onClick={remove} className="text-xs text-gray-600 hover:text-red-400 ml-auto">배경 제거</button>
      </div>
    </div>
  );
}

function EmployeeModal({ admin, onSave, onClose }) {
  const [form, setForm] = useState({
    username: admin?.username || '',
    password: '',
    name: admin?.name || '',
    role: admin?.role || 'admin',
    department: admin?.department || '',
    team: admin?.team || '',
    rank: admin?.rank || '',
    isActive: admin?.isActive ?? true,
  });
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    try {
      if (admin?.id) {
        const payload = { name: form.name, role: form.role, isActive: form.isActive, department: form.department, team: form.team || null, rank: form.rank };
        if (form.password) payload.password = form.password;
        await api.put(`/admins/${admin.id}`, payload);
      } else {
        if (!form.username || !form.password) return setError('아이디와 비밀번호는 필수입니다.');
        await api.post('/admins', form);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.message || '저장 실패');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm">
        <h2 className="text-white font-bold mb-4">{admin?.id ? '직원 수정' : '직원 추가'}</h2>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">아이디</label>
            <input
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              disabled={!!admin?.id}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">
              비밀번호 {admin?.id && <span className="text-gray-600">(변경 시에만 입력)</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">이름</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">부서</label>
              <select
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value, team: '' }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
              >
                <option value="">선택</option>
                {DEPT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">직급</label>
              <select
                value={form.rank}
                onChange={e => setForm(f => ({ ...f, rank: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
              >
                <option value="">선택</option>
                {RANK_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {TEAM_OPTIONS[form.department] && (
            <div>
              <label className="text-gray-400 text-xs mb-1 block">팀</label>
              <select
                value={form.team}
                onChange={e => setForm(f => ({ ...f, team: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
              >
                <option value="">전체 (팀 미지정)</option>
                {TEAM_OPTIONS[form.department].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">시스템 권한</label>
            <select
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
            >
              <option value="admin">직원</option>
              <option value="superadmin">슈퍼관리자</option>
            </select>
          </div>
          {admin?.id && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                className="w-4 h-4 accent-brand"
              />
              <span className="text-gray-400 text-sm">활성화</span>
            </label>
          )}
          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          <button onClick={handleSave} className="flex-1 bg-brand hover:bg-brand/80 text-white rounded-lg py-2 text-sm font-medium">저장</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [modal, setModal] = useState(null);
  const me = JSON.parse(localStorage.getItem('mfg_user') || '{}');

  const load = () => api.get('/admins').then(r => setAdmins(r.data));
  useEffect(() => { load(); }, []);

  const del = async id => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/admins/${id}`);
    load();
  };

  return (
    <div>
      {me.role === 'superadmin' && <LoginBgUpload />}
      <PageHeader icon={Users} title="직원 관리">
        <button
          onClick={() => setModal({})}
          className="flex items-center gap-2 bg-brand hover:bg-brand/80 text-white rounded-lg px-4 py-2 text-sm font-medium"
        >
          <Plus size={16} /> 직원 추가
        </button>
      </PageHeader>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="text-left px-5 py-3">이름</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">부서</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">직급</th>
              <th className="text-left px-4 py-3">권한</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">마지막 로그인</th>
              <th className="text-right px-5 py-3">액션</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                <td className="px-5 py-3">
                  <p className="text-white font-medium">{a.name || '-'}</p>
                  <p className="text-gray-600 text-xs font-mono">{a.username}</p>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <p className="text-gray-400 text-sm">{a.department || '-'}</p>
                  {a.team && <p className="text-gray-600 text-xs">{a.team}</p>}
                </td>
                <td className="px-4 py-3 text-gray-400 hidden md:table-cell">{a.rank || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    a.role === 'superadmin' ? 'bg-brand/20 text-brand' : a.isActive ? 'bg-gray-700 text-gray-400' : 'bg-gray-800 text-gray-600'
                  }`}>
                    {a.role === 'superadmin' ? '슈퍼관리자' : '직원'}
                    {!a.isActive && ' (비활성)'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                  {a.lastLoginAt ? dayjs(a.lastLoginAt).format('YYYY-MM-DD HH:mm') : '없음'}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setModal(a)} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-700 mr-1">
                    <Pencil size={13} />
                  </button>
                  {a.id !== me.id && (
                    <button onClick={() => del(a.id)} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-700">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <EmployeeModal
          admin={modal?.id ? modal : null}
          onSave={() => { load(); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
